'use client'

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import { Plane, Trophy, Crown, Upload, Check } from 'lucide-react'

/**
 * FGL-Glider-Silhouette — schlanker Hochleistungs-Segler im Seitenprofil,
 * passend zum FGL-Brand. Statisch, gold-getoent, an currentColor gebunden.
 */
function FglGliderIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size * 1.6}
      height={size}
      viewBox="0 0 32 18"
      fill="currentColor"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      {/* Tragflaeche (lang + schlank wie ein 4m+-Segler) */}
      <path d="M1 9.4 Q3 8.8 8 8.6 L20 8.4 L25 8.5 L20 9.0 L8 9.4 L3 9.6 Z" />
      {/* Rumpf */}
      <path d="M19 8.0 L25 8.6 Q26.5 9 25 9.4 L19 9.5 Z" />
      {/* Hoehenleitwerk */}
      <path d="M24 8.4 L28 7.6 L29 8.0 L29 9.3 L28 9.6 L24 9.0 Z" />
      {/* Seitenleitwerk */}
      <path d="M26.2 8.4 L27.5 6.5 L28 6.6 L27.5 8.6 Z" />
      {/* Cockpit-Haube */}
      <circle cx="20.5" cy="8.4" r="0.9" />
    </svg>
  )
}
import { FglHeader } from '@/components/layout/fgl-header'
import { useTranslation } from '@/i18n/client'
import {
  LenisProvider,
  Reveal,
  Magnetic,
  GliderBackdrop,
  GliderSwarm,
} from '@/components/award'

// 2026-05-29: Tester-Bewerbung entfernt (Frist 2026-05-14 ausgelaufen). Public-User
// sehen bei deaktivierter Registrierung jetzt einen Login-Button — bestehende
// Tester loggen sich mit ihrem FGL-Code ein, alle anderen warten auf öffentliche
// Registrierung ab 03.06.2026.

// Jeder Plan-Feature ist ein Paar [titleKey, tooltipKey]. Der Tooltip wird via
// data-tip auf den <li> ausgespielt — der globale FloatingTooltip liest das.
// `null` als tooltipKey heisst „kein Tooltip" (z.B. Sammel-Bullets wie
// „Alles aus Start").
type FeatureRow = [string, string | null]

const planDefs: Array<{
  code: string
  minFlights: number
  icon: any
  accent: string
  highlighted?: boolean
  descKey: string
  features: FeatureRow[]
  ctaKey: string
}> = [
  {
    code: 'start', minFlights: 0, icon: Plane, accent: '#9ca3af',
    descKey: 'plan.startDesc',
    // Tooltip-Keys mappen auf featureTooltips.start.f<n> — Single Source of
    // Truth, geteilt mit /features. Mapping orientiert sich an der
    // inhaltlichen Reihenfolge: pricing-newStart09 entspricht features-startF10
    // (3D-Luftraum), pricing-newStart11 ↔ features-startF9 (Newsletter).
    features: [
      ['plan.newStart01', 'featureTooltips.start.f1'],
      ['plan.newStart02', 'featureTooltips.start.f2'],
      ['plan.newStart03', 'featureTooltips.start.f3'],
      ['plan.newStart04', 'featureTooltips.start.f4'],
      ['plan.newStart05', 'featureTooltips.start.f5'],
      ['plan.newStart06', 'featureTooltips.start.f6'],
      ['plan.newStart07', 'featureTooltips.start.f7'],
      ['plan.newStart08', 'featureTooltips.start.f8'],
      ['plan.newStart09', 'featureTooltips.start.f10'],
      ['plan.newStart10', 'featureTooltips.start.f11'],
      ['plan.newStart11', 'featureTooltips.start.f9'],
      ['plan.newStart12', 'featureTooltips.start.f15'],
    ],
    ctaKey: 'plan.startCta',
  },
  {
    code: 'pro', minFlights: 25, icon: Trophy, accent: 'var(--award-gold)', highlighted: true,
    descKey: 'plan.proDesc',
    features: [
      ['plan.allFromStart', null],
      ['plan.newPro01', 'featureTooltips.pro.f1'],
      ['plan.newPro02', 'featureTooltips.pro.f2'],
      ['plan.newPro03', 'featureTooltips.pro.f3'],
      ['plan.newPro04', 'featureTooltips.pro.f4'],
      ['plan.newPro05', 'featureTooltips.pro.f5'],
      ['plan.newPro06', 'featureTooltips.pro.f6'],
      ['plan.newPro07', 'featureTooltips.pro.f7'],
      ['plan.newPro08', 'featureTooltips.pro.f8'],
      ['plan.newPro09', 'featureTooltips.pro.f9'],
      ['plan.newPro10', 'featureTooltips.pro.f10'],
    ],
    ctaKey: 'plan.proCta',
  },
  {
    code: 'premium', minFlights: 100, icon: Crown, accent: '#b45309',
    descKey: 'plan.premiumDesc',
    features: [
      ['plan.allFromPro', null],
      ['plan.newPremium01', 'featureTooltips.premium.f1'],
      ['plan.newPremium02', 'featureTooltips.premium.f2'],
      ['plan.newPremium03', 'featureTooltips.premium.f3'],
      ['plan.newPremium04', 'featureTooltips.premium.f4'],
      ['plan.newPremium05', 'featureTooltips.premium.f5'],
      ['plan.newPremium06', 'featureTooltips.premium.f6'],
      ['plan.newPremium07', 'featureTooltips.premium.f7'],
      ['plan.newPremium08', 'featureTooltips.premium.f8'],
      ['plan.newPremium09', 'featureTooltips.premium.f9'],
      ['plan.newPremium10', 'featureTooltips.premium.f10'],
      ['plan.newPremium11', 'featureTooltips.premium.f11'],
      ['plan.newPremium12', 'featureTooltips.premium.f12'],
    ],
    ctaKey: 'plan.premiumCta',
  },
]

export default function PricingPage() {
  const { t } = useTranslation()
  // FAQ-Section 2026-05-08 nach /faq verschoben (User-Wunsch). Pricing bleibt
  // schlank: Plan-Cards + Donate + CTA. Antworten zu Wertung/Thermik/Bulk-Upload
  // findet der User unter https://free-glide-league.com/faq.

  // Admin-Toggle: registrierungsgesteuerte Plan-CTAs. Wenn Registrierung
  // gerade aus ist (Tester-Phase 14.05.2026 vor Public-Launch 03.06.2026),
  // schalten wir die „Jetzt starten / Pro freischalten / Premium freischalten"-
  // Buttons zu einem Tester-Mailto um, statt den User auf eine geschlossene
  // /register-Seite zu kicken.
  const [registrationOnline, setRegistrationOnline] = useState<boolean | null>(null)
  useEffect(() => {
    fetch('/api/public/site-state')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setRegistrationOnline(d?.registrationEnabled === true))
      .catch(() => setRegistrationOnline(false))
  }, [])
  const showOfflineMode = registrationOnline === false

  return (
    <LenisProvider>
      <div data-award="true" className="min-h-screen relative overflow-x-clip" style={{ background: 'var(--bg-primary)' }}>
        <GliderBackdrop />
        <FglHeader />

        {/* HERO — kompakt + asymmetrisch (FAQ-R4-Pattern) */}
        <section className="relative z-10 pt-[100px] pb-10 md:pt-[120px] md:pb-14 px-6 sm:px-10">
          <div className="max-w-[1400px] mx-auto">
            <Reveal>
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="award-eyebrow">{t('pricing.pageEyebrow')}</span>
                <span className="h-px w-16 bg-[var(--award-line-hi)]" />
                <span className="award-label">{t('pricing.pageEyebrowSub')}</span>
              </div>
            </Reveal>

            <div className="grid grid-cols-12 gap-6 md:gap-12 items-end">
              <div className="col-span-12 md:col-span-7">
                <Reveal delay={1}>
                  <h1 className="award-h-mega">
                    {t('pricing.title').split(' ').slice(0, -1).join(' ')}{' '}
                    <span className="award-grad-text">
                      {t('pricing.title').split(' ').pop() || 'Plans.'}
                    </span>
                  </h1>
                </Reveal>
              </div>
              <div className="col-span-12 md:col-span-5">
                <Reveal delay={2}>
                  <p className="award-body-lead">{t('pricing.subtitle')}</p>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        {/* PLAN CARDS */}
        <section className="relative z-10 py-12 md:py-16">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-0 items-stretch">
              {planDefs.map((plan, idx) => {
                const PlanIcon = plan.icon
                return (
                  <React.Fragment key={plan.code}>
                    {idx > 0 && (
                      <div className="hidden md:flex items-center justify-center px-4" style={{ perspective: '600px' }}>
                        <Reveal delay={(idx) as 1 | 2 | 3}>
                          <div className="award-coin" style={{ animation: 'awardCoinFlip 5s linear infinite', transformStyle: 'preserve-3d' }}>
                            <div className="award-coin-face">
                              <span className="award-coin-num">{idx === 1 ? '25' : '100'}</span>
                              <span className="award-coin-arrow">→</span>
                            </div>
                            <div className="award-coin-face award-coin-back">
                              <span className="award-coin-label">{t('pricing.coinLabelLine1')}<br/>{t('pricing.coinLabelLine2')}</span>
                            </div>
                          </div>
                        </Reveal>
                      </div>
                    )}
                    <Reveal delay={(idx + 1) as 1 | 2 | 3}>
                      <div
                        className={`award-plan-card ${plan.highlighted ? 'is-highlighted' : ''} ${idx > 0 ? 'mt-4 md:mt-0' : ''}`}
                        style={{ '--plan-accent': plan.accent } as React.CSSProperties}
                      >
                        <div className="flex items-center gap-3 mb-5">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ background: 'color-mix(in oklch, var(--plan-accent) 14%, transparent)' }}
                          >
                            <PlanIcon size={22} style={{ color: 'var(--plan-accent)' }} />
                          </div>
                          <div>
                            <h3 className="award-h-md">{t(`plan.${plan.code}`)}</h3>
                            <p className="text-[12px] mt-1" style={{ color: 'var(--award-cirrus-3)' }}>
                              {t(plan.descKey)}
                            </p>
                          </div>
                        </div>

                        <div className="mb-6 pb-6" style={{ borderBottom: '1px solid var(--award-line)' }}>
                          <div className="flex items-baseline gap-2">
                            <span
                              className="font-bold leading-none"
                              style={{
                                fontFamily: 'var(--font-display)',
                                fontSize: 'clamp(56px, 7vw, 88px)',
                                letterSpacing: '-0.05em',
                                color: 'var(--plan-accent)',
                              }}
                            >
                              {plan.minFlights === 0 ? '0' : plan.minFlights}
                            </span>
                            {plan.minFlights > 0 && (
                              <span
                                className="plan-flights-badge"
                                style={{ color: 'var(--plan-accent)' }}
                              >
                                <FglGliderIcon size={14} />
                                {t('plan.validFlights')}
                              </span>
                            )}
                          </div>
                          {plan.minFlights === 0 && (
                            <p
                              className="plan-flights-badge mt-2"
                              style={{ color: 'var(--plan-accent)' }}
                            >
                              <FglGliderIcon size={14} />
                              {t('plan.start')}
                            </p>
                          )}
                        </div>

                        <Magnetic>
                          {showOfflineMode ? (
                            // 2026-05-29: Registrierung offline → Login statt Tester-Bewerbungs-Mail
                            <Link
                              href="/login"
                              className={`w-full text-center inline-flex items-center justify-center gap-2 mb-6 ${plan.highlighted ? 'award-btn award-btn-gold' : 'award-btn award-btn-ghost'}`}
                            >
                              <Upload size={14} />
                              {t('pricing.ctaTesterApply')}
                            </Link>
                          ) : (
                            <Link
                              href="/register"
                              className={`w-full text-center inline-flex items-center justify-center gap-2 mb-6 ${plan.highlighted ? 'award-btn award-btn-gold' : 'award-btn award-btn-ghost'}`}
                            >
                              <Upload size={14} />
                              {t(plan.ctaKey)}
                            </Link>
                          )}
                        </Magnetic>

                        <ul className="space-y-2.5 flex-1">
                          {plan.features.map(([titleKey, tooltipKey], i) => (
                            <li
                              key={i}
                              className="flex gap-2.5 items-start"
                              data-tip={tooltipKey ? t(tooltipKey) : undefined}
                              style={tooltipKey ? { cursor: 'help' } : undefined}
                            >
                              <Check size={15} className="shrink-0 mt-1" style={{ color: 'var(--plan-accent)' }} />
                              <span className="text-[13px] leading-snug" style={{ color: 'var(--award-cirrus-2)' }}>
                                {t(titleKey)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Reveal>
                  </React.Fragment>
                )
              })}
            </div>
          </div>
        </section>

        {/* DONATE-Section am 2026-05-08 nach /donate verschoben — auf /pricing
            (= „Mitmachen") nur noch die Tier-Stufen-Erklaerung. Donate hat
            eigene Public-Seite mit voller Atmosphaere. */}

        {/* FAQ-Link */}
        <section className="relative z-10 py-8 md:py-10 text-center">
          <Reveal>
            <Link
              href="/faq"
              className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest"
              style={{ color: 'var(--award-gold)', letterSpacing: '0.15em' }}
            >
              {t('pricing.ctaReadFaq')} <span aria-hidden>→</span>
            </Link>
          </Reveal>
        </section>

        {/* CLOSING — Tester-Aufruf statt offene Registrierung (Public-Launch erst 03.06.2026) */}
        <section className="relative z-10 py-16 md:py-20">
          <div className="award-thermal-dot" style={{ left: '15%', top: '30%' }} />
          <div className="award-thermal-dot" style={{ left: '85%', top: '60%', animationDelay: '1.5s' }} />
          <div className="max-w-[1100px] mx-auto px-6 text-center relative z-10">
            <Reveal>
              <h3 className="award-h-lg mb-8">
                {t('pricing.closingTitle1')} <span className="award-grad-text">{t('pricing.closingTitleAccent')}</span>
              </h3>
            </Reveal>
            <Reveal delay={1}>
              <p className="text-[14px] mb-8 max-w-xl mx-auto" style={{ color: 'var(--award-cirrus-2)' }}>
                {/* Date-Werte werden bewusst inline gehalten (im Template):
                    `<s>` markiert Hervorhebungen — ersetze durch <strong>. */}
                {(() => {
                  const raw = t('pricing.closingTesterLine', { testerStart: '14.05.2026', publicLaunch: '03.06.2026' })
                  // Template kann <s>...</s> enthalten — wir parsen das einfach
                  // mit einer Split-Strategie statt dangerouslySetInnerHTML.
                  const parts = raw.split(/<s>|<\/s>/)
                  return parts.map((part, i) => i % 2 === 1
                    ? <strong key={i} style={{ color: 'var(--award-gold)' }}>{part}</strong>
                    : <span key={i}>{part}</span>
                  )
                })()}
              </p>
            </Reveal>
            <Reveal delay={2}>
              {/* 2026-06-19 Pillen-Polish: Closing-CTAs auf wertung-tab-pill
                  Pattern. Primary=active (Gold-Glas), Secondary=inactive. Magnetic-
                  Wrapper bleibt für den Magnet-Hover-Effekt erhalten. */}
              <div className="flex gap-4 justify-center flex-wrap">
                <Magnetic>
                  <Link
                    href="/login"
                    data-state="active"
                    className="wertung-tab-pill inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-[12px] font-mono font-bold uppercase tracking-[0.08em]"
                  >
                    {t('pricing.closingCtaApply')} <span aria-hidden>→</span>
                  </Link>
                </Magnetic>
                <Magnetic>
                  <Link
                    href="/features"
                    data-state="inactive"
                    className="wertung-tab-pill inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-[12px] font-mono font-bold uppercase tracking-[0.08em]"
                  >
                    {t('pricing.closingCtaFeatures')}
                  </Link>
                </Magnetic>
              </div>
            </Reveal>
          </div>
        </section>

        <style jsx global>{`
          /* „valide Flüge"-Badge — prominenter, glühend, klar als Flugzahl
             (nicht als Währung!) lesbar. Plan-Accent-Farbe + Pulse-Glow. */
          .plan-flights-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-family: var(--font-display);
            font-weight: 900;
            font-size: 13px;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            text-shadow: 0 0 12px color-mix(in oklch, currentColor 60%, transparent);
            animation: planFlightsPulse 2.6s ease-in-out infinite;
          }
          @keyframes planFlightsPulse {
            0%, 100% {
              text-shadow: 0 0 8px color-mix(in oklch, currentColor 35%, transparent),
                           0 0 16px color-mix(in oklch, currentColor 18%, transparent);
              filter: drop-shadow(0 0 4px color-mix(in oklch, currentColor 25%, transparent));
            }
            50% {
              text-shadow: 0 0 14px color-mix(in oklch, currentColor 75%, transparent),
                           0 0 28px color-mix(in oklch, currentColor 45%, transparent);
              filter: drop-shadow(0 0 8px color-mix(in oklch, currentColor 55%, transparent));
            }
          }

          @keyframes awardCoinFlip {
            0%, 100% { transform: rotateY(0deg); }
            50% { transform: rotateY(180deg); }
          }
          .award-coin {
            position: relative;
            width: 72px;
            height: 72px;
            transform-style: preserve-3d;
          }
          .award-coin-face {
            position: absolute;
            inset: 0;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: radial-gradient(circle at 30% 30%, var(--award-gold-soft), var(--award-ink-3));
            border: 2px solid var(--award-gold);
            box-shadow:
              0 4px 18px var(--award-gold-glow),
              inset 0 2px 0 rgba(255,255,255,0.3),
              inset 0 -2px 0 rgba(0,0,0,0.1);
            backface-visibility: hidden;
          }
          .award-coin-back {
            transform: rotateY(180deg);
            background: radial-gradient(circle at 70% 70%, var(--award-gold-soft), var(--award-ink-3));
          }
          .award-coin-num {
            font-family: var(--font-display);
            font-size: 22px;
            font-weight: 700;
            color: var(--award-gold);
            letter-spacing: -0.04em;
          }
          .award-coin-arrow {
            font-size: 16px;
            color: var(--award-gold);
            margin-left: 2px;
          }
          .award-coin-label {
            font-family: var(--font-mono);
            font-size: 9px;
            font-weight: 700;
            color: var(--award-gold);
            letter-spacing: 0.18em;
            text-transform: uppercase;
            text-align: center;
            line-height: 1.1;
          }

          .award-plan-card {
            display: flex;
            flex-direction: column;
            background: var(--award-ink-2);
            border: 1.5px solid var(--award-line-hi);
            border-radius: 20px;
            padding: 30px 28px;
            transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
            min-height: 580px;
          }
          .award-plan-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 16px 48px -22px rgba(20,32,42,0.25);
            border-color: var(--plan-accent);
          }
          .award-plan-card.is-highlighted {
            background: var(--award-ink-3);
            border-color: var(--award-gold);
            box-shadow: 0 16px 48px -22px var(--award-gold-glow);
            transform: scale(1.02);
          }
        `}</style>
      </div>
    </LenisProvider>
  )
}
